import type { ReportStrategyResponse } from "../types/reports";
import type { StrategyActivePineSummary } from "../types/reportStrategyPine";
import {
  getExitHookReadinessDisplay,
  getInjectReadinessDisplay,
  getParseStatusDisplay,
  isIndicatorPineScript,
  shouldShowInjectReadinessBadge,
} from "./pineIrUi";
import {
  activePineProvenanceParts,
  formatActivePineProvenanceBadgeTitle,
  formatPineParseStatusLabel,
  strategyHasMasterPinescriptUploaded,
  strategyHasPineSignals,
  strategyHasTradingViewPinescriptCreated,
} from "./reportStrategyPineUi";
import { mergeStrategyWithParseSummary } from "./strategyComposeUi";
import type { StrategyPineBadgeVariant } from "../views/Dashboard/Strategies/strategyTablePineBadges";

export type ComposeChecklistItemState = "done" | "warning" | "todo" | "blocked";

export type ComposeChecklistItem = {
  id: string;
  title: string;
  description: string;
  state: ComposeChecklistItemState;
  badgeLabel?: string;
  badgeVariant?: StrategyPineBadgeVariant;
  pineBadgeKind?: string;
};

export type ActiveTradingViewComposeChecklist = {
  phase: "before" | "after";
  heading: string;
  subheading?: string;
  items: ComposeChecklistItem[];
  readyToCompose: boolean;
};

function parseChecklistItem(merged: ReportStrategyResponse): ComposeChecklistItem {
  const status = merged.pine_parse_status;
  const ux = getParseStatusDisplay(status);

  if (!status) {
    return {
      id: "parse",
      title: "Parse master Pinescript",
      description: "Run Parse in Edit strategy so Tessera can read entries, exits, and script kind.",
      state: "todo",
      badgeLabel: "Not parsed",
      badgeVariant: "pine-status-not-parsed",
      pineBadgeKind: "status-not-parsed",
    };
  }

  if (status === "failed") {
    return {
      id: "parse",
      title: "Fix Pinescript parse errors",
      description: ux?.description ?? "Parse failed. Merge and compose are blocked until errors are fixed.",
      state: "blocked",
      badgeLabel: formatPineParseStatusLabel(status),
      badgeVariant: "pine-parse-failed",
      pineBadgeKind: "parse-failed",
    };
  }

  if (status === "partial") {
    return {
      id: "parse",
      title: "Review parse warnings",
      description: ux?.description ?? "Script parsed with warnings. You can compose, but review issues in Edit strategy.",
      state: "warning",
      badgeLabel: formatPineParseStatusLabel(status),
      badgeVariant: "pine-parse-partial",
      pineBadgeKind: "parse-partial",
    };
  }

  return {
    id: "parse",
    title: "Master Pinescript parsed",
    description: ux?.description ?? "Pinescript parsed successfully.",
    state: "done",
    badgeLabel: formatPineParseStatusLabel(status),
    badgeVariant: "pine-parse-success",
    pineBadgeKind: "parse-full",
  };
}

function masterChecklistItem(merged: ReportStrategyResponse): ComposeChecklistItem {
  const uploaded = strategyHasMasterPinescriptUploaded(merged);
  const signalsOnly = !uploaded && strategyHasPineSignals(merged);

  if (uploaded) {
    return {
      id: "master",
      title: "Master Pinescript uploaded",
      description: "Your source TradingView script is saved on this strategy.",
      state: "done",
      badgeLabel: "Uploaded",
      badgeVariant: "pine-master-script",
      pineBadgeKind: "master-uploaded",
    };
  }

  if (signalsOnly) {
    return {
      id: "master",
      title: "Upload master Pinescript",
      description: "Parse metadata exists without script text. Save your Pinescript in Edit strategy.",
      state: "warning",
      badgeLabel: "Signals only",
      badgeVariant: "pine-status-signals-only",
      pineBadgeKind: "status-signals-only",
    };
  }

  return {
    id: "master",
    title: "Upload master Pinescript",
    description: "Add your original TradingView script in Edit strategy before composing an export.",
    state: "todo",
    badgeLabel: "Not uploaded",
    badgeVariant: "pine-master-none",
    pineBadgeKind: "master-not-uploaded",
  };
}

function injectChecklistItem(merged: ReportStrategyResponse): ComposeChecklistItem | null {
  const isIndicator = isIndicatorPineScript(merged);
  const inject = merged.inject_readiness;

  if (!inject) {
    return {
      id: "inject",
      title: "Inject readiness",
      description: "Parse master Pinescript first. Tessera will evaluate whether policy can merge into entries.",
      state: "todo",
      badgeLabel: "Inject unknown",
      badgeVariant: "pine-status-inject-unknown",
      pineBadgeKind: "status-inject-unknown",
    };
  }

  if (!shouldShowInjectReadinessBadge(inject) && inject === "not_applicable") {
    return {
      id: "inject",
      title: isIndicator ? "Indicator compose path" : "Entry merge not used",
      description:
        getInjectReadinessDisplay(inject)?.description ??
        "Use Append policy to indicator or Separate indicator when composing.",
      state: "done",
      badgeLabel: "Entry merge off",
      badgeVariant: "pine-inject-na",
      pineBadgeKind: "inject-not_applicable",
    };
  }

  const ux = getInjectReadinessDisplay(inject);

  if (inject === "ready") {
    return {
      id: "inject",
      title: "Ready for policy merge",
      description: ux?.description ?? "Policy can merge into your trade entries when you choose merge mode.",
      state: "done",
      badgeLabel: "Ready for merge",
      badgeVariant: "pine-status-ready-for-merge",
      pineBadgeKind: "status-ready-for-merge",
    };
  }

  if (inject === "needs_review") {
    return {
      id: "inject",
      title: "Review inject sites",
      description:
        ux?.description ??
        "Some entries need review. You can still compose with merge or separate-indicator modes.",
      state: "warning",
      badgeLabel: ux?.shortLabel ?? "Needs review",
      badgeVariant: "pine-inject-review",
      pineBadgeKind: "inject-needs_review",
    };
  }

  if (inject === "unsupported") {
    return {
      id: "inject",
      title: "Entry merge unsupported",
      description:
        ux?.description ??
        "Use Separate indicator or Append policy to indicator when composing. Merge into strategy is not available.",
      state: "warning",
      badgeLabel: ux?.shortLabel ?? "Unsupported",
      badgeVariant: "pine-inject-unsupported",
      pineBadgeKind: "inject-unsupported",
    };
  }

  return null;
}

function exitHookChecklistItem(merged: ReportStrategyResponse): ComposeChecklistItem | null {
  const readiness = merged.exit_hook_readiness;
  if (!readiness) return null;

  const ux = getExitHookReadinessDisplay(readiness);
  let state: ComposeChecklistItemState = "todo";
  let variant: StrategyPineBadgeVariant = "pine-exit-none";

  if (readiness === "ready") {
    state = "done";
    variant = "pine-exit-ready";
  } else if (readiness === "partial") {
    state = "warning";
    variant = "pine-exit-partial";
  } else if (readiness === "unsupported") {
    state = "warning";
    variant = "pine-exit-unsupported";
  } else if (readiness === "not_applicable") {
    state = "done";
    variant = "pine-exit-na";
  }

  return {
    id: "exit-hooks",
    title: "Exit hooks",
    description: ux?.description ?? "Exit hook readiness for strategy.exit patching.",
    state,
    badgeLabel: ux?.shortLabel,
    badgeVariant: variant,
    pineBadgeKind: `exit-${readiness}`,
  };
}

function policyVersionChecklistItem(activePolicyVersionCount: number): ComposeChecklistItem {
  if (activePolicyVersionCount > 0) {
    return {
      id: "policy",
      title: "Policy version saved",
      description:
        activePolicyVersionCount === 1
          ? "You have a saved policy version to attach when composing."
          : `You have ${activePolicyVersionCount} active policy versions. Pick one (or combine) in Compose.`,
      state: "done",
      badgeLabel: `${activePolicyVersionCount} active`,
      badgeVariant: "pine-master-script",
      pineBadgeKind: "policy-saved",
    };
  }

  return {
    id: "policy",
    title: "Save a policy version",
    description:
      "Run a report simulation and save the policy (or combine versions). Compose attaches that ruleset to your script.",
    state: "todo",
    badgeLabel: "None yet",
    badgeVariant: "pine-compose-none",
    pineBadgeKind: "policy-none",
  };
}

function composedChecklistItem(
  merged: ReportStrategyResponse,
  active?: StrategyActivePineSummary | null
): ComposeChecklistItem {
  const status = active?.status;
  const hasComposed = strategyHasTradingViewPinescriptCreated(merged);

  if (status === "stale") {
    return {
      id: "composed",
      title: "TradingView Pinescript needs refresh",
      description:
        formatActivePineProvenanceBadgeTitle(active) ??
        "Master Pinescript changed after your last compose. Re-compose to update downloads.",
      state: "warning",
      badgeLabel: "Outdated",
      badgeVariant: "pine-compose-stale",
      pineBadgeKind: "compose-stale",
    };
  }

  if (hasComposed || status === "composed") {
    const provenance = activePineProvenanceParts(active);
    const detail = provenance
      ? `Composed via ${provenance.composeModeLabel}${provenance.tail ? ` · ${provenance.tail}` : ""}.`
      : "Composed TradingView export is saved on this strategy.";
    return {
      id: "composed",
      title: "TradingView Pinescript composed",
      description: detail,
      state: "done",
      badgeLabel: "Created",
      badgeVariant: "pine-status-active-compose",
      pineBadgeKind: "status-active-compose",
    };
  }

  return {
    id: "composed",
    title: "Compose TradingView Pinescript",
    description: "Open Compose, attach a policy version, and save the generated export here.",
    state: "todo",
    badgeLabel: "Not created",
    badgeVariant: "pine-compose-none",
    pineBadgeKind: "compose-not-created",
  };
}

function indicatorOverlayItem(merged: ReportStrategyResponse): ComposeChecklistItem | null {
  if (!merged.has_indicator_pine) return null;
  return {
    id: "indicator-overlay",
    title: "Indicator overlay saved",
    description: "Separate-indicator compose also saved an overlay script for TradingView.",
    state: "done",
    badgeLabel: "Indicator overlay saved",
    badgeVariant: "pine-status-indicator-overlay",
    pineBadgeKind: "status-indicator-overlay",
  };
}

export function isReadyToComposeTradingViewPine(
  strategy: ReportStrategyResponse,
  activePolicyVersionCount: number
): boolean {
  const merged = mergeStrategyWithParseSummary(strategy);
  if (!strategyHasMasterPinescriptUploaded(merged)) return false;
  const parse = merged.pine_parse_status;
  if (!parse || parse === "failed") return false;
  if (activePolicyVersionCount < 1) return false;
  return true;
}

const COMPOSE_BLOCKER_ITEM_IDS = ["master", "parse", "policy"] as const;

/** Checklist rows that still block TradingView compose (master, parse, policy only). */
export function getActiveTradingViewComposeRemainingSteps(
  checklist: ActiveTradingViewComposeChecklist
): ComposeChecklistItem[] {
  if (checklist.readyToCompose) return [];
  return checklist.items.filter(
    (item) =>
      (COMPOSE_BLOCKER_ITEM_IDS as readonly string[]).includes(item.id) && item.state !== "done"
  );
}

export function activeTradingViewComposeRemainingTone(
  items: ComposeChecklistItem[]
): "info" | "warning" | "error" {
  if (items.some((item) => item.state === "blocked")) return "error";
  if (items.some((item) => item.state === "warning")) return "warning";
  return "info";
}

/** Prerequisites and outcomes for the Active TradingView Pinescript dialog. */
export function buildActiveTradingViewComposeChecklist(
  strategy: ReportStrategyResponse,
  options: {
    activePolicyVersionCount: number;
    currentPolicyVersionId?: string | null;
  }
): ActiveTradingViewComposeChecklist {
  const merged = mergeStrategyWithParseSummary(strategy);
  const active = merged.active_pine;
  const hasComposed = strategyHasTradingViewPinescriptCreated(merged);
  const phase: "before" | "after" = hasComposed ? "after" : "before";

  const prerequisiteItems: ComposeChecklistItem[] = [
    masterChecklistItem(merged),
    parseChecklistItem(merged),
    policyVersionChecklistItem(options.activePolicyVersionCount),
  ];

  const injectItem = injectChecklistItem(merged);
  if (injectItem) prerequisiteItems.push(injectItem);

  const exitItem = exitHookChecklistItem(merged);
  if (exitItem) prerequisiteItems.push(exitItem);

  if (phase === "before") {
    const readyToCompose = isReadyToComposeTradingViewPine(merged, options.activePolicyVersionCount);
    const blocked = prerequisiteItems.some((item) => item.state === "blocked");
    return {
      phase: "before",
      heading: "Before you compose",
      subheading:
        "All requirements below must be complete before you compose your TradingView Pinescript.",
      items: prerequisiteItems,
      readyToCompose: readyToCompose && !blocked,
    };
  }

  const afterItems: ComposeChecklistItem[] = [...prerequisiteItems, composedChecklistItem(merged, active)];

  const overlay = indicatorOverlayItem(merged);
  if (overlay) afterItems.push(overlay);

  const stale = active?.status === "stale";

  return {
    phase: "after",
    heading: stale ? "Compose again to refresh" : "Compose checklist",
    subheading: stale
      ? "Your export is outdated after master Pinescript changes. Re-compose when you are ready."
      : "What you completed to generate this TradingView export.",
    items: afterItems,
    readyToCompose: isReadyToComposeTradingViewPine(merged, options.activePolicyVersionCount),
  };
}
